import { CloseOutlined, NumberOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { CreditCardIcon, DevicesIcon } from 'assets/images/svg';
import { CREDENTIAL_TYPE_PASSCODE } from 'config/credentialTypes';
import dayjs from 'dayjs';
import { groupCredentialsByPhysicalIdentifier, groupCredentialsBySourceForPerson } from 'library/helpers/accessControl';
import React, { useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectCredentialsByPersonID, selectLoadingCredentialIDs, selectCredentials } from 'reduxStore/credentials/selectors';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { selectPermissionManageableDevices } from 'reduxStore/devices/selectors';
import { selectPermissionsByPersonID } from 'reduxStore/permissions/selectors';
import CredentialDrawer from 'zComponents/molecules/CredentialDrawer';
import { Empty, Flex, Popover, Spin, Tag, Tooltip, Typography } from 'zui/Antd';

const { Text } = Typography;

const CredentialPanelContent = ({ personID, isEditing }) => {
	const {
		token: { fontWeightStrong, colorIcon, paddingSM, paddingXXS, colorTextDescription }
	} = theme.useToken();
	const dispatch = useDispatch();

	// ✅ Get credentials assigned to the person
	const credentials = useSelector((state) => selectCredentialsByPersonID(state, personID));
	const allCredentials = useSelector(selectCredentials);
	const permissions = useSelector((state) => selectPermissionsByPersonID(state, personID));
	const devices = useSelector(selectPermissionManageableDevices);
	const loadingCredentialIDs = useSelector(selectLoadingCredentialIDs);
	const credentialPropagation = useSelector((state) => state.Credentials.credentialPropagation, shallowEqual);

	// Keep credentials visible while they are loading OR have active propagation progress,
	// even if they no longer belong to this person after a refetch.
	// Only include credentials that were/are related to this person (not other persons' ops).
	const visibleCredentials = useMemo(() => {
		const activeIDs = new Set([
			...Object.keys(loadingCredentialIDs).map(Number),
			...Object.keys(credentialPropagation).map(Number)
		]);
		const inFlight = [...activeIDs]
			.filter((id) => !credentials.some((c) => c.credentialID === id))
			.map((id) => allCredentials.find((c) => c.credentialID === id))
			.filter((c) => c && (c.personID === personID || !c.personID))
		return [...credentials, ...inFlight];
	}, [credentials, allCredentials, loadingCredentialIDs, credentialPropagation, personID]);

	const [openCredentialDrawer, setOpenCredentialDrawer] = React.useState(false);
	const [selectedCredential, setSelectedCredential] = React.useState(null);

	// ✅ Remove credential association
	const handleRemoveCredential = (credentialID) => {
		dispatch(
			credentialsActions.unassignCredential({
				credentialID,
				data: { oldPersonID: personID }
			})
		);
	};

	const displayItems = groupCredentialsBySourceForPerson({
		credentials: groupCredentialsByPhysicalIdentifier(visibleCredentials),
		devices,
		permissions
	});

	return (
		<Flex wrap gap={paddingSM}>
			{visibleCredentials.length === 0 ? (
				<div
					style={{
						width: '100%',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center'
					}}
				>
					<Empty description='No access credentials' image={Empty.PRESENTED_IMAGE_SIMPLE} />
				</div>
			) : (
				displayItems.map((credential) => {
					const isPasscode = credential.credentialTypeID === CREDENTIAL_TYPE_PASSCODE;
					const isLoading = !!loadingCredentialIDs[credential.credentialID];
					const isZeroDate = (d) => !d || (typeof d === 'string' && d.startsWith('0001-01-01'));
					const isPermanent =
						isZeroDate(credential.startDate) || isZeroDate(credential.endDate);
					const validityLabel = isPermanent
						? 'Permanent'
						: `${dayjs(credential.startDate).format('DD/MM/YY')} — ${dayjs(credential.endDate).format('DD/MM/YY')}`;

					return (
						<Flex key={credential.credentialID} vertical>
							<Popover
								trigger='hover'
								placement='bottom'
								title={
									<Flex vertical gap={2}>
										<span>{credential.tooltip}</span>
										<Text type='secondary' style={{ fontSize: 11 }}>
											{validityLabel}
										</Text>
									</Flex>
								}
								content={
									credential.lockNames?.length > 0 ? (
										<Flex vertical gap={4} style={{ maxWidth: 250, maxHeight: 363, overflowY: 'auto' }}>
											{credential.lockNames.map((name, i) => (
												<Flex key={i} align='center' gap={6}>
													<DevicesIcon color={colorTextDescription} />
													<Text style={{ fontSize: 12 }}>{name}</Text>
												</Flex>
											))}
										</Flex>
									) : null
								}
							>
								<Spin spinning={isLoading} size='small'>
									<Tag
										color='magenta'
										style={{
											fontWeight: fontWeightStrong,
											borderColor: 'currentColor',
											cursor: isLoading ? 'default' : 'pointer',
											marginInlineEnd: 0,
											opacity: isLoading ? 0.5 : 1
										}}
										onClick={() => {
											setSelectedCredential(credential.credentialID);
											setOpenCredentialDrawer(true);
										}}
									>
										<Flex align='center' gap={paddingXXS}>
											{isPasscode ? (
												<>
													<NumberOutlined />
													{credential.name} - {credential.value}
												</>
											) : (
												<>
													<CreditCardIcon color='currentColor' />
													{credential.name} - {credential.value}
												</>
											)}

											{isEditing && (
												<Tooltip title='Unassign credential'>
													<CloseOutlined
														style={{
															cursor: 'pointer',
															color: colorIcon
														}}
														onClick={(e) => {
															e.stopPropagation();
															handleRemoveCredential(
																credential.credentialID,
																credential.credentialTypeID
															);
														}}
													/>
												</Tooltip>
											)}
										</Flex>
									</Tag>
								</Spin>
							</Popover>
						</Flex>
					);
				})
			)}
			<CredentialDrawer
				setVisible={setOpenCredentialDrawer}
				visible={openCredentialDrawer}
				credentialID={selectedCredential}
			/>
		</Flex>
	);
};

export default CredentialPanelContent;
